import React, { useState, useCallback } from 'react';
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Typography, FormControl, InputLabel, Select } from '@mui/material';
import { Print, MoreVert } from '@mui/icons-material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useNavigate } from 'react-router-dom';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
  GroupQueryOptions,
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { DataService } from 'src/services/data-service';
import { showAlert } from 'src/redux/alert/alertReducer';
import { LabelPaperSize, usePrint } from 'src/hooks/usePrint';

interface BoxInterface {
  barcode: string;
  storageUnitId: string;
  storageUnitName: string;
  warehouseId: string;
  warehouseName: string;
  totalQuantities: number;
  totalProducts: number;
}

const BoxListing: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<BoxInterface | null>(null);
  const [showZeros, setShowZeros] = useState(false);
  const [printPaperSize, setPrintPaperSize] = useState<LabelPaperSize>('SMALL_5x2_5');
  const user = useAppSelector(s => s.user.user);
  const userRole = user?.role as ROLE;
  const userId = user?.id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { printBoxLabel, isPrinting, formatBoxDataForPrinting } = usePrint();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, row: BoxInterface) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handlePrintLabel = async () => {
    if (!selectedRow) return;
    
    try {
      const printOptions = formatBoxDataForPrinting({
        barcode: selectedRow.barcode,
        warehouseName: selectedRow.warehouseName,
        storageUnitName: selectedRow.storageUnitName,
        totalProducts: selectedRow.totalProducts,
        totalQuantities: selectedRow.totalQuantities,
         labelPaperSize: printPaperSize as LabelPaperSize
      });
      
      await printBoxLabel(printOptions);
      dispatch(showAlert({ message: 'Label printed successfully!', type: 'success' }));
    } catch (error) {
      dispatch(showAlert({ message: 'Failed to print label!', type: 'error' }));
    }
    handleMenuClose();
  };

  const mapFieldForGrouping = useCallback((field: string): string => {
    const fieldMapping: Record<string, string> = {
      'barcode': 'Barcode',
      'warehousename': 'WarehouseName',
      'storageunitname': 'StorageUnitName',
      'totalproducts': 'TotalProducts',
      'totalquantities': 'TotalQuantities',
      'warehouseid': 'WarehouseId',
      'storageunitid': 'StorageUnitId',
    };
    
    return fieldMapping[field.toLowerCase()] || field;
  }, []);

  const fetchBoxes = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
      grouping?: GroupQueryOptions;
    }) => {
      const isGrouped = params.grouping && params.grouping.GroupBy.length > 0;
      
      const queryParams = new URLSearchParams({
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize),
      });

      if (params.search) queryParams.append('SearchTerm', params.search);
      if (showZeros) queryParams.append('includeZeros', 'true');
      if (userRole === ROLE.CLIENT && userId) queryParams.append('clientId', userId);
      
      let res: Response;

      if (isGrouped && params.grouping) {
        // POST request for grouped data with query parameters and empty payload
        params.grouping.GroupBy.forEach(field => {
          queryParams.append('GroupBy', mapFieldForGrouping(field));
        });
        queryParams.append('Level', String(params.grouping.Level));
        queryParams.append('GroupSortBy', params.grouping.GroupSortBy || 'key');
        queryParams.append('GroupSortDescending', String(params.grouping.GroupSortDescending || false));
        queryParams.append('IncludeCount', String(params.grouping.IncludeCount || true));
        
        if (params.grouping.ParentKey) {
          queryParams.append('ParentKey', params.grouping.ParentKey);
        }

        res = await DataService.get(`api/BoxManagement/grouped?${queryParams.toString()}`, {});
      } else {
        // GET request for normal data
        if (params.sortModel?.length > 0) {
          const { field, sort } = params.sortModel[0];
          queryParams.append('SortBy', field);
          queryParams.append('SortDescending', String(sort === 'desc'));
        }

        res = await DataService.get(`api/BoxManagement/all-barcodes?${queryParams.toString()}`);
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch boxes`);
      const json = await res.json();

      let items: BoxInterface[] = [];
      let groups: any[] = [];
      
      if (isGrouped) {
        if (json?.data?.groups) {
          groups = Array.isArray(json.data.groups) ? json.data.groups : Object.values(json.data.groups);
        } else if (Array.isArray(json?.groups)) {
          groups = json.groups;
        }
      } else {
        if (json?.data?.items) {
          if (Array.isArray(json.data.items)) items = json.data.items;
          else if (typeof json.data.items === 'object') items = Object.values(json.data.items);
        } else if (Array.isArray(json?.data)) {
          items = json.data;
        } else if (Array.isArray(json?.items)) {
          items = json.items;
        }
      }

      return {
        items: isGrouped ? undefined : items,
        groups: isGrouped ? groups : undefined,
        totalCount: json?.data?.totalCount ?? json?.totalCount ?? (isGrouped ? groups.length : items.length),
        totalGroups: isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : undefined,
        pageNumber: json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : (json?.data?.totalCount ?? json?.totalCount ?? items.length)) / params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false,
      };
    },
    [mapFieldForGrouping, showZeros, userRole, userId]
  );

  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'barcode',
      headerName: 'Barcode',
      flex: 1,
      minWidth: 150,
      groupable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 'medium' }}
          onClick={() => navigate(`/Box/${params.value}`)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'warehouseName',
      headerName: 'Warehouse',
      flex: 1.2,
      minWidth: 140,
      groupable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 'medium' }}
          onClick={() => navigate(`/warehouse/${params.row.warehouseId}`)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'storageUnitName',
      headerName: 'Storage Unit',
      flex: 1.2,
      minWidth: 140,
      groupable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 'medium' }}
          onClick={() => navigate(`/storage-unit/${params.row.storageUnitId}`)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'totalProducts',
      headerName: 'Total Products',
      width: 130,
      type: 'number',
      groupable: false,
    },
    {
      field: 'totalQuantities',
      headerName: 'Total Quantities',
      width: 140,
      type: 'number',
      groupable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      groupable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, params.row)}
            disabled={isPrinting}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const actions: PaginatedDataGridAction[] = [
    {
      key: 'toggle-zeros',
      title: showZeros ? 'Hide Zero Quantities' : 'Show Zero Quantities',
      icon: showZeros ? <FilterAltOffIcon /> : <FilterAltIcon />,
      onClick: () => { setShowZeros(p => !p); setRefreshKey(p => p + 1); },
    },
  ];

  const handleRowClick = (params: any) => navigate(`/Box/${params.row.barcode}`);

  return (
    <Box p={3}>
      <PaginatedDataGrid
        key={refreshKey}
        title="Boxes"
        columns={columns}
        idField="barcode"
        useBackendPagination
        onDataFetch={fetchBoxes}
        cacheTimeout={300000}
        actions={actions}
        enableGrouping={true}
        enableGroupDrillDown={true}
        fieldMapper={mapFieldForGrouping}
        wmsMode
        groupedUrl='api/BoxManagement/grouped/'
        densityLevel="compact" 
        striped
        autoHeight={true}
        compactEmptyState={true}
        responsiveColumns={true}
        hideFooterWhenEmpty={true}
        maxHeight={700}
        onRowClick={handleRowClick}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
      />
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem disabled>
          <ListItemText 
            primary={
              <FormControl size="small" fullWidth>
                <InputLabel>Paper Size</InputLabel>
                <Select
                   value={printPaperSize}
                  onChange={(e) => setPrintPaperSize(e.target.value as LabelPaperSize)}
                  label="Paper Size"
                  onClick={(e) => e.stopPropagation()}
                >
                   <MenuItem value="SMALL_5x2_5">Small 5×2.5 (barcode only)</MenuItem>
                  <MenuItem value="A4">A4</MenuItem>
                  <MenuItem value="A5">A5</MenuItem>
                </Select>
              </FormControl>
            }
          />
        </MenuItem>
        <MenuItem onClick={handlePrintLabel} disabled={isPrinting}>
          <ListItemIcon>
            <Print fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={isPrinting ? "Printing..." : "Print Label"} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BoxListing;